import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import { useAssetContext } from '@/context/AssetContext';
import SEO from '@/components/SEO';
import { 
  Target, 
  GraduationCap, 
  FileText, 
  PenTool,
  Plane,
  Banknote,
  Home,
  BookOpen,
  Users,
  Award,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  Shield,
  HeartHandshake,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = window.location.origin;

// Icon mapping for services from CMS
const iconMap = {
  'target': Target,
  'graduation-cap': GraduationCap,
  'file-text': FileText,
  'pen-tool': PenTool,
  'plane': Plane,
  'banknote': Banknote,
  'home': Home,
  'book-open': BookOpen,
  'users': Users,
  'award': Award,
  'shield': Shield,
  'heart-handshake': HeartHandshake,
  'default': Target
};

// Static fallback services data
const staticServices = [
  {
    id: 'profile-assessment',
    icon: 'target',
    title: 'Profile Assessment',
    short_description: 'Expert evaluation of your academic profile and career goals',
    full_description: 'Our expert counselors conduct a comprehensive analysis of your academic background, test scores, work experience, and career aspirations.',
    features: ['Detailed academic profile evaluation', 'Career goal alignment', 'Personalized recommendations'],
    color: 'cyan',
    is_featured: true
  },
  {
    id: 'university-shortlisting',
    icon: 'graduation-cap',
    title: 'University Shortlisting',
    short_description: 'AI-powered matching with universities',
    full_description: 'Using our advanced AI-powered matching algorithm combined with expert insights.',
    features: ['AI-powered matching', 'Budget-aligned options', 'Scholarship identification'],
    color: 'violet',
    is_featured: false
  },
  {
    id: 'application-support',
    icon: 'file-text',
    title: 'Application Support',
    short_description: 'End-to-end guidance for applications',
    full_description: 'From document preparation to final submission, we guide you through every step.',
    features: ['Application strategy', 'Document preparation', 'Deadline tracking'],
    color: 'green',
    is_featured: false
  },
  {
    id: 'sop-lor',
    icon: 'pen-tool',
    title: 'SOP & LOR Writing',
    short_description: 'Professional writing assistance',
    full_description: 'Our expert writers help you craft compelling Statements of Purpose.',
    features: ['Personalized SOP drafting', 'Multiple revisions', 'LOR guidance'],
    color: 'amber',
    is_featured: false
  },
  {
    id: 'visa-assistance',
    icon: 'plane',
    title: 'Visa Assistance',
    short_description: '98% success rate',
    full_description: 'Expert visa guidance with documentation support and interview preparation.',
    features: ['Document preparation', 'Interview coaching', 'Application tracking'],
    color: 'violet',
    is_featured: false
  },
  {
    id: 'test-prep',
    icon: 'book-open',
    title: 'Test Preparation',
    short_description: 'IELTS, TOEFL, GRE, GMAT coaching',
    full_description: 'Comprehensive test preparation support including study materials and practice tests.',
    features: ['IELTS & TOEFL prep', 'GRE & GMAT coaching', 'Practice tests'],
    color: 'coral',
    is_featured: true
  },
  {
    id: 'education-loan',
    icon: 'banknote',
    title: 'Education Loan',
    short_description: 'Financial guidance & support',
    full_description: 'We help you navigate education loan options from banks and NBFCs.',
    features: ['Loan comparison', 'Documentation help', 'Bank liaison'],
    color: 'green',
    is_featured: false
  },
  {
    id: 'accommodation',
    icon: 'home',
    title: 'Accommodation',
    short_description: 'Find your home abroad',
    full_description: 'Assistance in finding safe and affordable accommodation near your university.',
    features: ['University housing', 'Private rentals', 'Verified listings'],
    color: 'cyan',
    is_featured: false
  },
  {
    id: 'pre-departure',
    icon: 'shield',
    title: 'Pre-Departure Support',
    short_description: 'Travel ready preparation',
    full_description: 'Complete pre-departure briefing including travel tips and cultural orientation.',
    features: ['Travel arrangements', 'Cultural orientation', 'Insurance guidance'],
    color: 'amber',
    is_featured: true
  },
];

const processSteps = [
  { step: '01', title: 'Free Consultation', desc: 'Book a free session to discuss your goals' },
  { step: '02', title: 'Profile Assessment', desc: 'We evaluate your academic profile' },
  { step: '03', title: 'University Selection', desc: 'AI-powered matching with best-fit universities' },
  { step: '04', title: 'Application & Visa', desc: 'Complete support until you land abroad' },
];

export const ServicesPage = () => {
  const { isDark } = useTheme();
  const { getAsset } = useAssetContext();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch CMS content for the Services page
  const { content: cmsContent } = usePageContent('services');
  
  // Get CMS-managed content with fallbacks
  const heroContent = getSection(cmsContent, 'hero', {
    badge_text: 'Our Services',
    title: 'End-to-End Study Abroad',
    title_highlight: 'Support',
    subtitle: 'From initial counseling to landing in your dream university, we\'re with you at every step of your international education journey.'
  });
  
  const seoContent = getSection(cmsContent, 'seo', {
    title: 'Our Services',
    description: 'Comprehensive study abroad services from UniXperts - profile assessment, university shortlisting, application support, visa assistance, and more.',
    keywords: 'study abroad services, education consultancy, visa assistance, university applications, IELTS coaching'
  });
  
  // Get CMS process steps with fallback
  const cmsProcessSteps = getSection(cmsContent, 'process_steps', null);
  const processStepsContent = cmsProcessSteps && Array.isArray(cmsProcessSteps) && cmsProcessSteps.length > 0 
    ? cmsProcessSteps 
    : processSteps;

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/services`);
        if (res.ok) {
          const data = await res.json();
          if (data.services && data.services.length > 0) {
            // Transform API data to match our format
            const transformedServices = data.services.map(s => ({
              ...s,
              icon: s.icon || 'default',
              color: s.color || 'cyan',
              features: s.features || []
            }));
            setServices(transformedServices);
          } else {
            setServices(staticServices);
          }
        } else {
          setServices(staticServices);
        }
      } catch (e) {
        console.error('Failed to fetch services:', e);
        setServices(staticServices);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Scroll to hash anchor once services are rendered (dropdown link like /services#profile-assessment)
  useEffect(() => {
    if (loading || !location.hash) return;
    const id = location.hash.slice(1);
    // Wait for cards to be in the DOM
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [loading, location.hash, services]);

  const colorClasses = {
    cyan: 'from-ux-cyan to-ux-blue',
    violet: 'from-ux-violet to-ux-rose',
    coral: 'from-ux-coral to-ux-amber',
    green: 'from-ux-green to-ux-mint',
    amber: 'from-ux-amber to-ux-coral',
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={seoContent.title || "Study Abroad Services - Admission, Visa, IELTS, Scholarships"}
        description={seoContent.description || "Complete study abroad services: University admission guidance, student visa support, IELTS/GRE preparation, scholarship assistance, and accommodation help. Expert counselors for USA, UK, Canada, Australia."}
        keywords={seoContent.keywords || "study abroad services, university admission help, student visa assistance, IELTS preparation, scholarship guidance, overseas education services, study abroad counseling"}
        canonical="https://unixperts.com/services"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Education Consulting",
          "provider": {
            "@type": "Organization",
            "name": "UniXperts"
          },
          "areaServed": "India",
          "description": "Complete study abroad services including university admission, visa support, test preparation, and scholarship guidance"
        }}
      />
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="services-page">
      {/* Hero Section - Cinematic Lifestyle */}
      <section className="py-20 md:py-32 relative overflow-hidden min-h-[60vh] flex items-center">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src={getAsset('hero_services') || "https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/5c4459954a25e98d6c64c8eab3ff47d0e0cd2699498b9fd5f62366044a1fbbba.png"}
            alt="Students in collaborative brainstorming"
            className="w-full h-full object-cover"
          />
          {/* Cinematic gradient overlay */}
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#1a2830]/95 via-[#233842]/80 to-transparent' : 'bg-gradient-to-r from-ux-charcoal/95 via-ux-charcoal/80 to-ux-violet-dark/70'}`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">{heroContent.badge_text}</Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight">
              {heroContent.title}
              <span className="gradient-text-aurora"> {heroContent.title_highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              {heroContent.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const IconComponent = iconMap[service.icon] || iconMap.default;
              const isFeatured = service.is_featured || service.size === 'large';
              const description = service.full_description || service.description || service.short_description;
              const features = service.features || [];
              
              return (
                <div 
                  key={service.id}
                  id={service.slug || service.id}
                  data-testid={`service-card-${service.slug || service.id}`}
                  className={`glass-panel rounded-3xl p-8 group cursor-pointer hover:scale-[1.02] transition-all scroll-mt-24 ${
                    isFeatured ? 'md:col-span-2' : ''
                  }`}
                >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClasses[service.color] || colorClasses.cyan} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {service.title}
                </h3>
                <p className={`mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'} ${isFeatured ? 'text-lg' : ''}`}>
                  {description}
                </p>
                
                {/* Features */}
                {features.length > 0 && (
                  <div className="space-y-2 mb-6">
                    {features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle2 className={`w-4 h-4 ${isDark ? 'text-ux-cyan' : 'text-ux-green'}`} />
                        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className={`py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
              How It Works
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Your Journey to
              <span className="gradient-text-electric"> Success</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {processStepsContent.map((step, i) => (
              <div key={i} className="relative">
                {/* Connector line */}
                {i < processSteps.length - 1 && (
                  <div className={`hidden md:block absolute top-8 left-1/2 w-full h-0.5 ${isDark ? 'bg-white/10' : 'bg-ux-charcoal/10'}`} />
                )}
                
                <div className="relative text-center">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-ux-cyan to-ux-violet flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl`}>
                    {step.step}
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {step.title}
                  </h3>
                  <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className={`mb-6 ${isDark ? 'bg-ux-amber/20 text-ux-amber' : 'bg-ux-amber/10 text-ux-amber'}`}>
                Why Choose Us
              </Badge>
              <h2 className={`text-3xl md:text-5xl font-bold mb-6 tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                We're Different.
                <span className="gradient-text-sunset"> Here's Why.</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Award, title: '98% Visa Success Rate', desc: 'Industry-leading success rate in visa approvals' },
                  { icon: Users, title: '30,000+ Students Placed', desc: 'Join our community of successful students worldwide' },
                  { icon: HeartHandshake, title: 'End-to-End Support', desc: "We're with you from first call to first day at university" },
                  { icon: Sparkles, title: 'AI-Powered Matching', desc: 'Smart technology to find your perfect university fit' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ux-cyan to-ux-blue flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{item.title}</h4>
                      <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/5c4459954a25e98d6c64c8eab3ff47d0e0cd2699498b9fd5f62366044a1fbbba.png"
                  alt="Students collaborating on success"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className={`absolute -bottom-6 -right-6 p-6 rounded-2xl glass-panel ${isDark ? 'bg-[#233842]/90' : 'bg-white/90'} shadow-xl`}>
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <img 
                        key={i}
                        src={`https://i.pravatar.cc/40?img=${i+20}`}
                        alt=""
                        className="w-8 h-8 rounded-full border-2 border-white"
                      />
                    ))}
                  </div>
                  <div>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Join 30,000+</p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Happy students</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section removed to avoid duplication —
          the Footer already renders a universal "Ready to Start Your Journey?"
          conversion strip above it on every page. */}
    </div>
    </>
  );
};

export default ServicesPage;
